import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import TokenManager, { TokenType } from '@/utils/tokenManager';
import { tokenRefreshService } from '@/utils/tokenRefreshService';
import { afterLoginPath } from '@/utils/loginReturn';
import { updateInfoUser } from './useGoftino';

export type SignInEvent = 'login' | 'registration';

const isDevicePWA = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari
  (window.navigator as any).standalone === true ||
  // Android Chrome
  document.referrer.includes('android-app://');

/**
 * Everything a finished sign-in does, for every way in (OTP, QR, Google, Google registration):
 * store the session, start refreshing it, report it to Matomo, hand the user to Goftino and go
 * to the page that sent them to login (or the dashboard). QR and Google used to stop after the
 * first step, so their sessions were never refreshed and never counted.
 */
const useFinishSignIn = () => {
  const navigate = useNavigate();
  const { trackEvent } = useMatomo();

  return useCallback(
    async (tokens: TokenType, event: SignInEvent, returnTo?: unknown) => {
      await TokenManager.set(tokens);
      tokenRefreshService.scheduleRefresh();

      // Analytics and the support chat must never keep a signed-in user on the code page.
      try {
        const pwa = isDevicePWA();
        trackEvent({
          category: pwa ? 'pwa' : 'web',
          action: event,
          name: event,
          customDimensions: [
            { id: pwa ? 3 : 1, value: TokenManager.parsed().accessToken?.UUID }
          ]
        });
        updateInfoUser();
      } catch {
        // Reported nowhere on purpose: the sign-in itself succeeded.
      }
      navigate(afterLoginPath(returnTo), { replace: true });
    },
    [navigate, trackEvent]
  );
};

export default useFinishSignIn;
